import { useState, useEffect, useRef } from "react"
import { FileSpreadsheet, Info, Loader2, Mail } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from "@/components/ui/dialog"

import { ClassroomPicker } from "../ClassRooms/ClassroomPicker"

import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import { SubjectCombobox } from "./SubjectCombobox"

// ─────────────────────────────────────────────────────────────────────────
// Embeddable "Export Register" action — self-contained: its own classroom
// picker, subject choice, and From/To date range, independent of whatever
// the main registry list happens to be filtered to. Generation itself now
// happens server-side (FileRegistry job pipeline, no size limit) — this
// component only ever kicks the job off and tells the user to expect an
// email, it never builds or downloads a workbook directly anymore.
//
// Classroom first. Then its attendance policy decides: a classroom that takes
// attendance without a subject exports its whole-class registers straight
// away; otherwise the subject is picked with the searchable picker (server
// search over the whole subject list, This programme tab for the classroom's
// programme), or "No subject (whole class)" for its no-subject registers.
// ─────────────────────────────────────────────────────────────────────────

type ClassroomOption = { id: string; name: string; programme_id?: string | null }

async function apiCall<T>(
    segment: "attendance" | "classrooms",
    path: string,
    params: Record<string, any> = {}
): Promise<T> {
    const q = Object.entries(params)
        .filter(([ , v ]) => v !== undefined && v !== null && v !== "")
        .map(([ k, v ]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join("&")
    const url = `${environment.BASE_URL}/${segment}/${path}${q ? `?${q}` : ""}`
    const res = await fetch(url, { headers: apiHeaders() as HeadersInit })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

async function apiPost<T>(path: string, body: any): Promise<T> {
    const url = `${environment.BASE_URL}/attendance/${path}`
    const res = await fetch(url, {
        method: "POST",
        headers: apiHeaders() as HeadersInit,
        body: JSON.stringify(body),
    })
    const json = await res.json()
    if (!json.success) throw new Error(json.message || "Request failed")
    return json
}

export default function ExportRegisterAction() {
    const [ open, setOpen ] = useState(false)

    // classroom (ClassroomPicker: department → programme, or search)
    const [ selectedClassroom, setSelectedClassroom ] = useState<ClassroomOption | null>(null)

    const [ subjectCode, setSubjectCode ] = useState("")
    // the chosen classroom's policy: no subject at all -> whole-class export
    const [ noSubjectPolicy, setNoSubjectPolicy ] = useState(false)
    // "No subject (whole class)" picked in the subject picker
    const [ wholeClass, setWholeClass ] = useState(false)
    const [ loadingClassroom, setLoadingClassroom ] = useState(false)
    const classroomSeq = useRef(0)

    const [ dateFrom, setDateFrom ] = useState("")
    const [ dateTo, setDateTo ] = useState("")

    const [ generating, setGenerating ] = useState(false)
    const [ resultMsg, setResultMsg ] = useState<string | null>(null)
    const [ resultIsError, setResultIsError ] = useState(false)

    useEffect(() => {
        setSubjectCode("")
        setWholeClass(false)
        setNoSubjectPolicy(false)
        if (!selectedClassroom) return
        const seq = ++classroomSeq.current
        setLoadingClassroom(true)
        apiCall<{ success: true; settings: { subject?: string }; source: { level: string } }>("attendance", "getResolvedPolicy", { classRoomId: selectedClassroom.id })
            .then(policy => {
                if (seq === classroomSeq.current) setNoSubjectPolicy(policy?.source?.level !== "default" && policy?.settings?.subject === "none")
            })
            .catch(() => { /* no answer: ask for a subject, as before */ })
            .finally(() => { if (seq === classroomSeq.current) setLoadingClassroom(false) })
    }, [ selectedClassroom ])

    const resetForm = () => {
        setSelectedClassroom(null)
        setSubjectCode("")
        setDateFrom("")
        setDateTo("")
        setResultMsg(null)
        setResultIsError(false)
    }

    // A no-subject policy exports the whole-class registers (no subject sent).
    const canGenerate = !!selectedClassroom && !loadingClassroom && (noSubjectPolicy || wholeClass || !!subjectCode) && !generating

    const handleGenerate = async () => {
        if (!canGenerate || !selectedClassroom) return
        setGenerating(true)
        setResultMsg(null)
        try {
            await apiPost<{ success: true; jobId: string; expiresAt: string }>("requestExport", {
                classRoomId: selectedClassroom.id,
                subjectCode: noSubjectPolicy || wholeClass ? null : subjectCode,
                dateFrom: dateFrom || null,
                dateTo: dateTo || null,
            })

            setResultIsError(false)
            setResultMsg("Export started — you'll get an email with a download link shortly. The link is valid for 2 days.")
        } catch (e: any) {
            setResultIsError(true)
            setResultMsg(e.message || "Export failed")
        } finally {
            setGenerating(false)
        }
    }

    return (
        <>
            <Button onClick={() => setOpen(true)} className="gap-2">
                <FileSpreadsheet className="h-4 w-4" />
                Export Register
            </Button>

            <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm() }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold">Export Register</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 py-2">
                        <div className="space-y-2">
                            <Label>Classroom</Label>
                            <ClassroomPicker value={selectedClassroom} onChange={setSelectedClassroom} />
                        </div>

                        {selectedClassroom && (
                            <div className="space-y-2">
                                <Label>Subject</Label>
                                {loadingClassroom ? (
                                    <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Checking this classroom…</p>
                                ) : noSubjectPolicy ? (
                                    <p className="flex items-start gap-2 rounded-md border bg-muted/40 p-2 text-sm text-muted-foreground">
                                        <Info className="mt-0.5 h-4 w-4 shrink-0" />
                                        This classroom's attendance policy takes attendance for the whole class, without a subject — the export covers all of it.
                                    </p>
                                ) : (
                                    <SubjectCombobox
                                        value={wholeClass ? "No subject (whole class)" : subjectCode}
                                        placeholder="Search subject by code or name…"
                                        programmeId={selectedClassroom.programme_id ?? null}
                                        allowClear
                                        clearLabel="No subject (whole class)"
                                        onClear={() => { setSubjectCode(""); setWholeClass(true) }}
                                        onSelect={(sub) => { setWholeClass(false); setSubjectCode(sub.subject_code) }}
                                    />
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>From date <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} />
                            </div>
                            <div className="space-y-2">
                                <Label>To date <span className="text-muted-foreground font-normal">(optional)</span></Label>
                                <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} />
                            </div>
                        </div>

                        <p className="text-xs text-muted-foreground">
                            One workbook for this classroom, subject and period: a summary to date, a tab per month, the register grid and an About sheet — with the classroom's attendance policy (its minimum and custom columns) when it has one. No size limit; the download link is emailed to you and stays valid for 2 days.
                        </p>

                        {resultMsg && (
                            <p className={`text-sm ${resultIsError ? "text-red-600" : "text-emerald-600"}`}>
                                {resultMsg}
                            </p>
                        )}
                    </div>

                    <DialogFooter className="gap-2 pt-2">
                        <Button variant="outline" onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button className="min-w-[180px] gap-2" onClick={handleGenerate} disabled={!canGenerate}>
                            {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            Generate & Email Me
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    )
}
